import React, { useEffect, useState,useMemo} from "react";
import { atom, useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import classes from "./Top.module.scss";
import Header from "../../molecules/header/Header";
import TopHeader from "../../organism/top-header/TopHeader";
import PointHeader from "../../organism/point-header/PointHeader";
import TopSummary from "../../organism/top-summary/TopSummary";
import rabbitOk from "../../static/images/rabbit-ok.png";
import mokomokoWhite from "../../static/images/mokomoko-white.png";
import mokomokoGray from "../../static/images/mokomoko-gray.png";
import TopContentsTitle from "../../organism/top-contents-title/TopContentsTitle";
import topBanner from "../../static/images/top/top-banner.png";
import newsBanner from "../../static/images/top/top-news-banner.png";
import { fetchRanking } from "../../api/RankingApi";
import DiagnosisCard from "../../organism/diagnosis-card/DiagnosisCard";
import QuizCard from "../../organism/quiz-card/QuizCard";
import BreadCrumb from "../../molecules/breadcrumb/BreadCrumb";
import { fetchUser } from "../../api/UsersApi";
import shindanText from "../../static/images/shindan-text.png";
import quizText from "../../static/images/quiz-text.png";
import { fetchUsersQuiz } from "../../api/UsersQuizApi";
import { fetchUsersBadge } from "../../api/UsersBadgeApi";
import { selectQuizAtom } from "../../atoms/quiz/Quiz";
import { fetchSimulationList } from "../../api/SimulationListApi";
import SimpleButton from "../../atoms/simple-button/SimpleButton";
import charJumpImage from "../../static/images/prepare/char-jump.png";

const Top = () => {
  const navigate = useNavigate();
  const rankingApi = fetchRanking();
  const userApi = fetchUser();
  const quizApi = fetchUsersQuiz();
  const badgeApi = fetchUsersBadge();
  const simulationApi = fetchSimulationList();
  const [selectQuiz, setSelectQuiz] = useAtom(selectQuizAtom);

  type RecommendQuizCard = {
    contentId: string;
    summary: string;
    tag: string;
    imageData: string;
    isChallenged?: boolean;
    totalCorrectNumber?: number;
  };

  // おすすめクイズの設定
  const RECOMMEND_IDS = new Set([
    "quiz0023",
    "quiz0025",
    "quiz0027",
    "quiz0028",
    "quiz0029",
  ]);
  const [recommendQuiz, setRecommendQuiz] = useState<RecommendQuizCard | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    rankingApi.fetch();
    simulationApi.fetch();
    userApi.fetch();
    quizApi.fetch({ quizClassification: 'all' });
    badgeApi.fetch({ isCompleted: false });
  }, []);

  const simPrepare = useMemo(() => {
    const list = simulationApi.state?.data;
    return list?.find(d => d.simulationId === "sim_prepare") ?? null;
  }, [simulationApi.state?.data]);

  useEffect(() => {
    const list = quizApi.state?.data?.[0]?.quizList ?? [];
    const candidates = list.filter((q) => RECOMMEND_IDS.has(q.quizId));
    if (candidates.length === 0) return;

    const picked = candidates[Math.floor(Math.random() * candidates.length)];
    setRecommendQuiz({
      contentId: picked.quizId,
      summary: picked.explanation,
      tag: picked.categoryName,
      imageData: picked.image,
      isChallenged: Boolean(picked.completedDate?.trim()),
      totalCorrectNumber: picked.totalCorrectNumber,
    });
  }, [quizApi.state?.data, badgeApi.state?.data]);

  const clickQuiz = (quizId: string) => {
    const usersQuiz = quizApi.state?.data?.[0].quizList.find(
      (d) => d.quizId === quizId
    );
    const usersBadge = badgeApi.state?.data?.[0].badgeList.find(
      (d) => d.quizId === quizId
    );
    if (usersQuiz == null || usersBadge == null) {
      return;
    }
    setSelectQuiz({
      quizId: usersQuiz.quizId,
      tag: usersQuiz.classificationName,
      title: usersQuiz.title,
      image: usersBadge.badgeImageData,
    });
    navigate("/quiz-contents");
  };

  return (
    <>
      <div className={classes.container}>
        <Header />
        <TopHeader currentMenuUrl="/top" />
        <PointHeader aquiredPoint={userApi.state?.data?.[0]?.currentPoint ?? 0} />
        <img className={classes.topHero} src={topBanner} alt="topHero" />
        <div className={classes.newsContents}>
          <div className={classes.newsSummary}>
            <TopSummary
              leftLogo={rabbitOk}
              mainTitle="NEWS！"
              rightLogo={mokomokoGray}
            />
            <div className={classes.newsBannerArea}>
              <img className={classes.newsImage} src={newsBanner} alt="NEWS" />
              <div className={classes.buttonArea}>
                <img className={classes.buttonImage} src={charJumpImage} alt="キャラ ジャンプ" />
                <SimpleButton
                  label="さっそくはじめる！"
                  width="90%"
                  color="orange"
                  isRadius={true}
                  onClick={() => navigate("/misa-top")}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={classes.recommendContents}>
          {userApi.state?.data?.[0].isWithin24hours ? (
            <>
              <div className={classes.summary}>
                <TopSummary
                  leftLogo={rabbitOk}
                  subTitle="初めての方向け"
                  mainTitle="まずはここからはじめましょう！"
                />
              </div>
              <div className={classes.block}>
                <div className={classes.title}>
                  <TopContentsTitle title={"未来への準備の第一歩"} />
                </div>
                <div className={classes.image}>
                  <img src={topBanner} alt={topBanner} />
                </div>
                <div className={classes.text}>
                  将来に必要なお金は、早めの“見える化”が大切です。みらいいコンシェルは、親子の夢と家族の未来を考える一歩をサポートします。
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={classes.summary}>
                <TopSummary
                  leftLogo={rabbitOk}
                  mainTitle="あなたにオススメ！"
                  rightLogo={mokomokoGray}
                />
              </div>
              <div className={classes.block}>
                <div className={classes.title}>
                  <TopContentsTitle image={shindanText} />
                </div>
                <div className={classes.content}>
                  {simPrepare && (
                    <DiagnosisCard
                      summary={simPrepare.summary}
                      tag={simPrepare.tag}
                      imageData={simPrepare.imageData}
                      internalUrl={simPrepare.internalUrl}
                      done={simPrepare.isChallenged}
                      type="diagnosis"
                      index={0}
                      expectedTime={simPrepare.expectedTime}
                    />
                  )}
                </div>
              </div>
              <div className={classes.block}>
                <div className={classes.title}>
                  <TopContentsTitle image={quizText}  />
                </div>
                <div className={classes.content}>
                  {recommendQuiz && (
                    <QuizCard
                      summary={recommendQuiz.summary}
                      tag={recommendQuiz.tag}
                      imageData={recommendQuiz.imageData}
                      callback={() => clickQuiz(recommendQuiz.contentId)}
                      internalUrl={""}
                      index={0}
                      done={recommendQuiz.isChallenged}
                      correctNo={recommendQuiz.totalCorrectNumber}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <div className={classes.rankingContents}>
          <div className={classes.summary}>
            <TopSummary
              leftLogo={mokomokoWhite}
              mainTitle="人気アクセスランキング"
              rightLogo={mokomokoGray}
            />
          </div>
          {rankingApi.state?.data?.map((d, index) => {
            const key = `${d.contentType}:${d.contentId}`;
            if (d.contentType === "simulation") {
              return (
                <div className={classes.rankingBlock} key={key}>
                  <DiagnosisCard
                    summary={d.summary}
                    tag={d.tag}
                    imageData={d.imageData}
                    callback={
                      d.externalUrl
                        ? () => navigate(`/diagnosis-external?type=${d.contentType}&id=${d.contentId}`)
                        : undefined
                    }
                    internalUrl={d.internalUrl}
                    type="ranking"
                    index={index}
                    done={d.isChallenged}
                    expectedTime={d.expectedTime}
                  />
                </div>
              );
            } else if (d.contentType === "quiz") {
              return (
                  <div className={classes.rankingBlock} key={key}>
                    <QuizCard
                      summary={d.summary}
                      tag={d.tag}
                      imageData={d.imageData}
                      callback={() => clickQuiz(d.contentId)}
                      internalUrl={""}
                      index={index}
                      done={d.isChallenged}
                      correctNo={d.totalCorrectNumber}
                      isRanking={true}
                    />
                  </div>
              );
            }
            return null;
          })}
        </div>
        <BreadCrumb
        data={[
            { displayName: "みらいいコンシェル" },
            { displayName: "TOPページ" },
          ]}
        />
      </div>
    </>
  );
};

export default Top;
